import React, { ChangeEvent, useState } from 'react';
import cn from 'classnames';
import { useDispatch, useSelector } from 'react-redux';

import { track } from 'apis/amethyst';
import { evSephoraBeautyInsider, SephoraInteraction } from 'events/sephora';
import { isValidEmailAddress } from 'helpers/index';
import { createSephoraUser, receiveSephoraError } from 'actions/sephora';
import useSephoraContext from 'hooks/useSephoraContext';
import { selectIsLoadingLoyalty } from 'selectors/sephora';
import { SmallLoader } from 'components/Loader';

import css from 'styles/components/sephora/beautyInsiderRegisterForm.scss';

const getBirthDayOptions = (selectedMonth?: string) => {
  const options = [
    <option key="-1" value="-1">
      Day
    </option>
  ];

  if (selectedMonth) {
    const month = parseInt(selectedMonth);
    const shortMonths = [4, 6, 9, 11];
    const daysInMonth = month === 2 ? 29 : shortMonths.includes(month) ? 30 : 31;

    for (let i = 1; i <= daysInMonth; i++) {
      options.push(
        <option key={i} value={i}>
          {i}
        </option>
      );
    }
  }

  return options;
};

const getBirthMonthOptions = () => {
  const options = [
    <option key="-1" value="-1" disabled>
      Month
    </option>
  ];

  for (let i = 1; i <= 12; i++) {
    options.push(
      <option key={i} value={i}>
        {i}
      </option>
    );
  }

  return options;
};

type RegistrationField = 'sephoraEmail' | 'firstName' | 'lastName' | 'birthDay' | 'birthMonth';

const BeautyInsiderRegisterForm = () => {
  const { sephoraEmail, setView } = useSephoraContext();
  const [formErrors, setFormErrors] = useState<RegistrationField[]>([]);
  const [email, setEmail] = useState(sephoraEmail);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [birthDay, setBirthDay] = useState<string>('-1');
  const [birthMonth, setBirthMonth] = useState<string>('-1');
  const isLoading = useSelector(selectIsLoadingLoyalty);
  const dispatch = useDispatch();

  const hasFieldError = (field: RegistrationField) => formErrors.indexOf(field) !== -1;

  const removeError = (field: RegistrationField) => {
    const errorIndex = formErrors.indexOf(field);

    if (errorIndex > -1) {
      const remainingErrors = [...formErrors.slice(0, errorIndex), ...formErrors.slice(errorIndex + 1)];

      setFormErrors(remainingErrors);
    }
  };

  const handleContinueClick = () => {
    const errors: RegistrationField[] = [];

    if (!isValidEmailAddress(email)) {
      errors.push('sephoraEmail');
    }

    if (!firstName) {
      errors.push('firstName');
    }

    if (!lastName) {
      errors.push('lastName');
    }

    if (birthMonth === '-1') {
      errors.push('birthMonth');
    }

    if (birthDay === '-1') {
      errors.push('birthDay');
    }

    setFormErrors(errors);

    if (errors.length) {
      return;
    }

    dispatch(createSephoraUser({ firstName, lastName, birthMonth: parseInt(birthMonth), birthDay: parseInt(birthDay), customerEmail: email }));
  };

  return isLoading ? (
    <SmallLoader />
  ) : (
    <>
      {sephoraEmail && (
        <div className={css.textContent}>
          <p className={css.copy}>No Beauty Insider account was found for {sephoraEmail}.</p>
          <p className={css.copy}>
            <button
              type="button"
              className={css.linkButton}
              onClick={() => {
                track(() => [evSephoraBeautyInsider, { interaction: SephoraInteraction.EDIT_EMAIL_BUTTON_CLICK }]);
                dispatch(receiveSephoraError(''));
                setView('linkEmail');
              }}
            >
              Edit email address
            </button>
            . You can create an account below.
          </p>
        </div>
      )}
      <div className={cn(css.formField, { [css.fieldError]: hasFieldError('sephoraEmail') })}>
        <label htmlFor={'sephoraEmail'}>Email Address</label>
        <input
          autoComplete="off"
          type="email"
          name={'sephoraEmail'}
          value={email}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            removeError('sephoraEmail');
            setEmail(e.target.value);
          }}
          placeholder="username@domain.com"
          inputMode="email"
        />
        {hasFieldError('sephoraEmail') && <div className={css.errorText}>Please enter e-mail address in the format username@domain.com</div>}
      </div>
      <div className={css.formFieldContainer}>
        <div className={cn(css.formField, { [css.fieldError]: hasFieldError('firstName') })}>
          <label htmlFor={'firstName'}>First Name</label>
          <input
            autoComplete="off"
            type="text"
            name={'firstName'}
            value={firstName}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              e.preventDefault();
              removeError('firstName');
              setFirstName(e.target.value);
            }}
            placeholder="First Name"
          />
          {hasFieldError('firstName') && <div className={css.errorText}>First name required. Please enter your first name.</div>}
        </div>
        <div className={cn(css.formField, { [css.fieldError]: hasFieldError('lastName') })}>
          <label htmlFor="lastName">Last Name</label>
          <input
            autoComplete="off"
            type="text"
            name="lastName"
            value={lastName}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              e.preventDefault();
              removeError('lastName');
              setLastName(e.target.value);
            }}
            placeholder="Last Name"
          />
          {hasFieldError('lastName') && <div className={css.errorText}>Last name required. Please enter your last name.</div>}
        </div>
      </div>
      <div className={css.formFieldContainer}>
        <div className={cn(css.formField, { [css.fieldError]: hasFieldError('birthMonth') })}>
          <label htmlFor={'birthMonth'}>Birth Month</label>
          <select
            className={cn(css.dropdown, { [css.unselected]: birthMonth === undefined })}
            name={'birthMonth'}
            value={birthMonth}
            onChange={(e: ChangeEvent<HTMLSelectElement>) => {
              removeError('birthMonth');
              setBirthMonth(e.target.value);
              setBirthDay('-1');
            }}
          >
            {getBirthMonthOptions()}
          </select>
          {hasFieldError('birthMonth') && <div className={css.errorText}>Birth month required. Please enter your birth month.</div>}
        </div>
        <div className={cn(css.formField, { [css.fieldError]: hasFieldError('birthDay') })}>
          <label htmlFor={'birthDay'}>Birth Day</label>
          <select
            className={cn(css.dropdown, { [css.unselected]: birthDay === undefined || birthDay === '-1' })}
            name={'birthDay'}
            value={birthDay}
            onChange={(e: ChangeEvent<HTMLSelectElement>) => {
              removeError('birthDay');
              setBirthDay(e.target.value);
            }}
          >
            {getBirthDayOptions(birthMonth)}
          </select>
          {hasFieldError('birthDay') && <div className={css.errorText}>Birth day required. Please enter your birth day.</div>}
        </div>
      </div>
      <button className={css.button} type="button" onClick={handleContinueClick}>
        Continue
      </button>
      <div className={css.acknowledgment}>
        By clicking “Continue” you acknowledge that you are a U.S. or Canada resident and (1) have read Sephora’s PRIVACY POLICY and NOTICE OF
        FINANCIAL INCENTIVE, (2) agree to TERMS OF USE, BEAUTY INSIDER TERMS, and to automatically receive Beauty Insider offers and notifications
        via email. You also acknowledge and agree that Zappos.com LLC may send your information to Sephora.
      </div>
    </>
  );
};

export default BeautyInsiderRegisterForm;
